import { Button, FormField, FormGrid, Page } from '@dixa/lib-shared-habla';
import { PhoneNumber } from 'lib-shared-legacy-types';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { numbersApi } from 'apis/numbers';
import { usersApi } from 'apis/users';
import { HookFormPhoneNumberSelect } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUser } from 'utils/user';

import { profileActions } from '../../../profile/settingsProfile.actions';

import styles from './preferencesGeneralForm.module.scss';

export type PreferencesGeneralFormFieldValues = {
  default_outbound_number?: [PhoneNumber];
};

export const PreferencesGeneralForm = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [updateUser] = usersApi.useUpdateUserMutation();
  const { user } = useCurrentUser();
  const userDefaultOutboundNumber = useAppSelector(
    (state) => state.settings.profile.profile.default_outbound_number,
  );
  const form = useForm<PreferencesGeneralFormFieldValues>({
    defaultValues: {
      default_outbound_number: undefined,
    },
  });
  const { control, setValue, handleSubmit, formState } = form;
  const notify = useNotifications();
  const organizationDefaultOutboundNumber = useAppSelector(
    (state) => state.settings.organization.organization.default_outbound_number,
  );
  const { defaultOutboundNumber } = numbersApi.useReadDefaultNumberQuery(
    undefined,
    {
      skip: !userDefaultOutboundNumber,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        defaultOutboundNumber: data?.data,
      }),
    },
  );

  // Sets the initial value of the default outbound number field to the user's preferred default. We
  // do this check because in case the user doesn't have a preferred default, the numbers API will
  // return the organization default if it exists, and we do not want to use that as the initial
  // value in the form, to prevent the organization default from being set as the user default.
  useEffect(() => {
    if (
      defaultOutboundNumber &&
      defaultOutboundNumber.e164_format === userDefaultOutboundNumber
    ) {
      setValue('default_outbound_number', [defaultOutboundNumber]);
      form.reset(form.getValues());
    }
  }, [defaultOutboundNumber, form, setValue, userDefaultOutboundNumber]);

  const onSubmit: SubmitHandler<PreferencesGeneralFormFieldValues> =
    useCallback(
      async (values) => {
        try {
          const { data } = await updateUser({
            id: user.id,
            patch: {
              updates: {
                ...(values.default_outbound_number?.length && {
                  default_outbound_number:
                    values.default_outbound_number[0].e164_format,
                }),
              },
              deletes: [
                ...(values.default_outbound_number?.length
                  ? []
                  : (['default_outbound_number'] as const)),
              ],
            },
          }).unwrap();

          dispatch(profileActions.update({ user: data }));

          notify(
            {
              title: 'Success',
              text: `Preferences have been updated.`,
              icon: 'done',
              time: 1500,
            },
            NotificationType.Success,
          );

          form.reset(values);
        } catch (error) {
          notify(
            {
              title: 'Error',
              text: `Preferences could not be updated.`,
              icon: 'exclamation',
              time: 1500,
            },
            NotificationType.Error,
          );
        }
      },
      [dispatch, form, notify, updateUser, user.id],
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Page.Section
        title="Outbound number"
        controls={
          <Button
            type="submit"
            isLoading={formState.isSubmitting}
            disabled={
              !formState.isValid || formState.isSubmitting || !formState.isDirty
            }
          >
            Save changes
          </Button>
        }
      >
        <FormGrid>
          <FormField span={isSmallScreen ? 6 : 3}>
            <HookFormPhoneNumberSelect
              name="default_outbound_number"
              label="Default outbound number"
              control={control}
              rules={{
                validate: {
                  isOutbound: (phoneNumber) =>
                    phoneNumber?.length
                      ? phoneNumber[0].outgoing_functionality ||
                        'The selected phone number does not support outbound calls'
                      : true,
                },
              }}
              componentProps={{
                showCountryFlag: true,
                clearable: true,
              }}
              helpIcon={
                !userDefaultOutboundNumber && organizationDefaultOutboundNumber
                  ? {
                      text: `If you don't select your preferred default outbound number, the organization's default outbound number will be used: ${organizationDefaultOutboundNumber}`,
                      dark: true,
                      tooltipClassName: styles.tooltip,
                    }
                  : undefined
              }
            />
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );
};
